import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    SimpleGrid,
    Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';

import { TradePlanDetailType } from '../../../_openapi/portfolio-optimizer';
import {
    booleanCol,
    dateCol,
    dateTimeCol,
    moneyCol,
    numberCol,
    textCol,
} from '../../../widgets/Table/columnHelpers/colDef';
import { MoneyCell } from '../../../widgets/Table/MoneyCell';
import { NumberCell } from '../../../widgets/Table/NumberCell';
import { DetailsBlock } from './DetailsBlock';
import {
    PaydayTradePlanCashflow,
    PaydayTradePlanPortfolio,
    PaydayTradePlanPosition,
    PaydayTradePlanPV,
    TradeDetailRecord,
} from './tradeDetailsModel';

export const PaydayDetails = ({
    tradePlanDetails,
}: {
    tradePlanDetails: Partial<Record<TradePlanDetailType, TradeDetailRecord>>;
}) => {
    const portfolio = tradePlanDetails[TradePlanDetailType.PORTFOLIO]?.detailJson?.[0];
    const positions = tradePlanDetails[TradePlanDetailType.POSITION]?.detailJson;
    const cashflows = tradePlanDetails[TradePlanDetailType.CASHFLOW]?.detailJson;
    const pvs = tradePlanDetails[TradePlanDetailType.PV]?.detailJson;

    return (
        <Accordion allowMultiple>
            {portfolio && <Portfolio portfolio={portfolio as PaydayTradePlanPortfolio} />}

            {positions && (
                <DetailsBlock
                    data={positions as PaydayTradePlanPosition[]}
                    columns={positionsColumns}
                    columnSettingsKey="paydayDetailsPositions"
                    title="Positions"
                    initialSort={{ id: 'sId', desc: true }}
                />
            )}

            {cashflows && (
                <DetailsBlock
                    data={cashflows as PaydayTradePlanCashflow[]}
                    columns={cashflowColumns}
                    columnSettingsKey="paydayDetailsCashflow"
                    title="Cashflow"
                    initialSort={{ id: 'cashflowDate', desc: false }}
                    paginate
                />
            )}

            {pvs && (
                <DetailsBlock
                    data={pvs as PaydayTradePlanPV[]}
                    columns={pvColumns}
                    columnSettingsKey="paydayDetailsPV"
                    title="PV"
                    initialSort={{ id: 'cashFlowMonth', desc: false }}
                    paginate
                />
            )}
        </Accordion>
    );
};

const Portfolio = ({ portfolio }: { portfolio: PaydayTradePlanPortfolio }) => {
    return (
        <AccordionItem>
            <AccordionButton background="whiteAlpha.50">
                <Box as="span" flex="1" textAlign="left">
                    Portfolio
                </Box>
                <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
                <SimpleGrid columns={4} columnGap={20}>
                    <Text fontSize="sm">NAV</Text>
                    <MoneyCell size="sm" value={portfolio.nav} />
                    <Text fontSize="sm">Cash</Text>
                    <MoneyCell size="sm" value={portfolio.cash} />

                    <Text fontSize="sm">Current PV</Text>
                    <MoneyCell size="sm" value={portfolio.currentPv} />
                    <Text fontSize="sm">Target PV</Text>
                    <MoneyCell size="sm" value={portfolio.targetPv} />

                    <Text fontSize="sm">Cash Futures Margin</Text>
                    <MoneyCell size="sm" value={portfolio.cashFuturesMargin} />
                    <Text fontSize="sm">Buffer Amount</Text>
                    <MoneyCell size="sm" value={portfolio.bufferAmount} />

                    <Text fontSize="sm">Fees Amount</Text>
                    <MoneyCell size="sm" value={portfolio.feesAmount} />
                    <Text fontSize="sm">Trade Quantity Sum</Text>
                    <NumberCell size="sm" value={portfolio.tradeQuantitySum} />

                    <Text fontSize="sm">CFI Amount</Text>
                    <MoneyCell size="sm" value={portfolio.cfiAmount} />
                    <Text fontSize="sm">Target CFI Securedness</Text>
                    <NumberCell size="sm" value={portfolio.targetCfiSecuredness} />

                    <Text fontSize="sm">Initial Duration</Text>
                    <NumberCell size="sm" value={portfolio.initialDuration} />
                    <Text fontSize="sm">Duration</Text>
                    <NumberCell size="sm" value={portfolio.duration} />

                    <Text fontSize="sm">Initial Fundedness</Text>
                    <NumberCell size="sm" value={portfolio.initialFundedness} />
                    <Text fontSize="sm">Fundedness</Text>
                    <NumberCell size="sm" value={portfolio.fundedness} />

                    <Text fontSize="sm">Initial Securedness</Text>
                    <NumberCell size="sm" value={portfolio.initialSecuredness} />
                    <Text fontSize="sm">Previous Target Securedness</Text>
                    <NumberCell size="sm" value={portfolio.previousTargetSecuredness} />

                    <Text fontSize="sm">Payday Amount</Text>
                    <MoneyCell size="sm" value={portfolio.paydayAmount} />
                </SimpleGrid>
            </AccordionPanel>
        </AccordionItem>
    );
};

const positionsColumnHelper = createColumnHelper<PaydayTradePlanPosition>();
const positionsColumns = [
    positionsColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    positionsColumnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    positionsColumnHelper.accessor('description', textCol({ id: 'description' })),
    positionsColumnHelper.accessor('accruedInterestShifted', moneyCol({ id: 'accruedInterestShifted' })),
    positionsColumnHelper.accessor('currentPv', moneyCol({ id: 'currentPv' })),
    positionsColumnHelper.accessor('targetPv', moneyCol({ id: 'targetPv' })),
    positionsColumnHelper.accessor('currentQuantity', numberCol({ id: 'currentQuantity' })),
    positionsColumnHelper.accessor('targetQuantity', numberCol({ id: 'targetQuantity' })),
    positionsColumnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity' })),
    positionsColumnHelper.accessor('tradeQuantityRounded', numberCol({ id: 'tradeQuantityRounded' })),
    positionsColumnHelper.accessor('quotePrice', moneyCol({ id: 'quotePrice' })),
    positionsColumnHelper.accessor('dirtyPrice', moneyCol({ id: 'dirtyPrice' })),
    positionsColumnHelper.accessor('priceSource', textCol({ id: 'priceSource' })),
    positionsColumnHelper.accessor('priceTimestamp', dateTimeCol({ id: 'priceTimestamp' })),
    positionsColumnHelper.accessor('contractMultiplier', numberCol({ id: 'contractMultiplier' })),
    positionsColumnHelper.accessor('priceMultiplier', numberCol({ id: 'priceMultiplier' })),
];

const cashflowColumnHelper = createColumnHelper<PaydayTradePlanCashflow>();
const cashflowColumns = [
    cashflowColumnHelper.accessor('cashflowType', textCol({ id: 'cashflowType' })),
    cashflowColumnHelper.accessor('cashflowDate', dateCol({ id: 'cashflowDate' })),
    cashflowColumnHelper.accessor('portfolioTargetEndDate', dateCol({ id: 'portfolioTargetEndDate' })),
    cashflowColumnHelper.accessor('portfolioTaxType', textCol({ id: 'portfolioTaxType' })),
    cashflowColumnHelper.accessor('withdrawalTaxRate', numberCol({ id: 'withdrawalTaxRate' })),
    cashflowColumnHelper.accessor('manuallySetWithdrawalTaxRate', booleanCol({ id: 'manuallySetWithdrawalTaxRate' })),
    cashflowColumnHelper.accessor('factorGuid', textCol({ id: 'factorGuid' })),
    cashflowColumnHelper.accessor('frequencyType', textCol({ id: 'frequencyType' })),
    cashflowColumnHelper.accessor('factorStartDate', dateCol({ id: 'factorStartDate' })),
    cashflowColumnHelper.accessor('factorEndDate', dateCol({ id: 'factorEndDate' })),
    cashflowColumnHelper.accessor('yearlyInflationRate', numberCol({ id: 'yearlyInflationRate' })),
    cashflowColumnHelper.accessor('inflationStartDate', dateCol({ id: 'inflationStartDate' })),
    cashflowColumnHelper.accessor('cashflowUnadjustedAmount', moneyCol({ id: 'cashflowUnadjustedAmount' })),
    cashflowColumnHelper.accessor('cashflowInflationMultiplier', numberCol({ id: 'cashflowInflationMultiplier' })),
    cashflowColumnHelper.accessor('cashflowInflatedAmount', moneyCol({ id: 'cashflowInflatedAmount' })),
    cashflowColumnHelper.accessor('cashflowGrossInflatedAmount', moneyCol({ id: 'cashflowGrossInflatedAmount' })),
    cashflowColumnHelper.accessor('cashflowFinalAmount', moneyCol({ id: 'cashflowFinalAmount' })),
    cashflowColumnHelper.accessor('cashFlowMonth', numberCol({ id: 'cashFlowMonth', format: { fractionDigits: 0 } })),
    cashflowColumnHelper.accessor('algoConfigEndDate', dateCol({ id: 'algoConfigEndDate' })),
    cashflowColumnHelper.accessor('active', booleanCol({ id: 'active' })),
    cashflowColumnHelper.accessor('rowId', numberCol({ id: 'rowId', format: { fractionDigits: 0 } })),
    cashflowColumnHelper.accessor('loadId', numberCol({ id: 'loadId', format: { fractionDigits: 0 } })),
];

const pvColumnHelper = createColumnHelper<PaydayTradePlanPV>();
const pvColumns = [
    pvColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    pvColumnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    pvColumnHelper.accessor('description', textCol({ id: 'description' })),
    pvColumnHelper.accessor('cashFlowMonth', numberCol({ id: 'cashFlowMonth', format: { fractionDigits: 0 } })),
    pvColumnHelper.accessor('cashflowFinalAmount', moneyCol({ id: 'cashflowFinalAmount' })),
    pvColumnHelper.accessor('pvMultiplier', numberCol({ id: 'pvMultiplier', format: { fractionDigits: 8 } })),
    pvColumnHelper.accessor(
        'targetCfiSecuredness',
        numberCol({ id: 'targetCfiSecuredness', format: { fractionDigits: 8 } }),
    ),
    pvColumnHelper.accessor(
        'pvMultiplierSecured',
        numberCol({ id: 'pvMultiplierSecured', format: { fractionDigits: 8 } }),
    ),
    pvColumnHelper.accessor('targetPv', moneyCol({ id: 'targetPv' })),
];
